import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  Select,
  InputNumber,
  DatePicker,
  Tag,
  Space,
  Tooltip,
  Popconfirm,
  Typography,
  message,
  Row,
  Col,
  Card,
  Divider,
  Descriptions,
  Empty
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  PrinterOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  InboxOutlined,
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import { utils, writeFile } from 'xlsx';
import { ordenesCompraService, proveedoresService, productosService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import OrdenCompraPrint from '../components/print/OrdenCompraPrint';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const METODOS_ENVIO = ['Paquetería', 'Entrega directa', 'Recolección en sucursal', 'Mensajería', 'Otro'];

const DIRECCION_ENTREGA_DEFAULT =
  'Calle Sur 2, MZ 4, LT 16, # 37. Ciudad Industrial. Tizayuca Hgo. HIDALGO MÉX.. C.P.:43805. R.F.C.:RAD081111EK7';

const STATUS_CONFIG = {
  creada:     { color: 'processing', label: 'Pend. Autorización' },
  autorizada: { color: 'blue',       label: 'Autorizada' },
  rechazada:  { color: 'error',      label: 'Rechazada' },
  entregada:  { color: 'success',    label: 'Entregada' },
  cancelada:  { color: 'default',    label: 'Cancelada' }
};

const formatCurrency = (amount) => {
  if (amount == null || amount === '') return '—';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
};

function OrdenesCompra() {
  const { user } = useAuth();
  const canCreate = ['root', 'admin', 'gerencia'].includes(user?.rol);
  const canAutorizar = ['root', 'gerencia'].includes(user?.rol);
  const canEntregar = ['root', 'admin', 'gerencia'].includes(user?.rol);
  const canDelete = user?.rol === 'root';

  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [proveedorFilter, setProveedorFilter] = useState(null);
  const [fechaRange, setFechaRange] = useState(null);

  const [proveedores, setProveedores] = useState([]);
  const [productosProveedor, setProductosProveedor] = useState([]);
  const [productosLoading, setProductosLoading] = useState(false);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedProveedorId, setSelectedProveedorId] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [productoToAdd, setProductoToAdd] = useState(null);
  const [editingOrden, setEditingOrden] = useState(null);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const numeroSugeridoRef = useRef('');
  const [form] = Form.useForm();

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailOrden, setDetailOrden] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [printData, setPrintData] = useState(null);
  const printRef = useRef(null);

  const triggerPrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Orden de Compra ${printData?.numero_orden || ''}`,
    pageStyle: `@page { size: A4; margin: 1.2cm; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }`,
    onAfterPrint: () => setPrintData(null)
  });

  useEffect(() => {
    if (printData === null) return;
    triggerPrint();
  }, [printData]); // eslint-disable-line

  // ── Fetch listado ──────────────────────────────────────────────────────
  const fetchOrdenes = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const s = 'search' in overrides ? overrides.search : search;
      const st = 'status' in overrides ? overrides.status : statusFilter;
      const prov = 'proveedor_id' in overrides ? overrides.proveedor_id : proveedorFilter;
      const range = 'fechaRange' in overrides ? overrides.fechaRange : fechaRange;

      const params = {};
      if (s) params.search = s;
      if (st) params.status = st;
      if (prov) params.proveedor_id = prov;
      if (range && range[0] && range[1]) {
        params.fecha_desde = range[0].format('YYYY-MM-DD');
        params.fecha_hasta = range[1].format('YYYY-MM-DD');
      }

      const res = await ordenesCompraService.getAll(params);
      setOrdenes(res.data.data || []);
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar órdenes de compra');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, proveedorFilter, fechaRange]);

  const fetchProveedores = useCallback(async () => {
    try {
      const res = await proveedoresService.getAll({ activo: 1 });
      setProveedores(res.data.data || []);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchOrdenes();
    fetchProveedores();
  }, [fetchOrdenes, fetchProveedores]);

  const handleSearch = (value) => {
    setSearch(value);
    fetchOrdenes({ search: value });
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value || '');
    fetchOrdenes({ status: value || '' });
  };

  const handleProveedorFilter = (value) => {
    setProveedorFilter(value || null);
    fetchOrdenes({ proveedor_id: value || null });
  };

  const handleFechaRangeFilter = (range) => {
    setFechaRange(range);
    fetchOrdenes({ fechaRange: range });
  };

  // ── Modal de creación / edición ─────────────────────────────────────────
  const fetchNumeroSugerido = async (proveedorId) => {
    try {
      const res = await ordenesCompraService.getSiguienteNumero(
        proveedorId ? { proveedor_id: proveedorId } : {}
      );
      const sugerido = res.data.data?.numero_orden || '';
      const actual = form.getFieldValue('numero_orden');
      if (!actual || actual === numeroSugeridoRef.current) {
        form.setFieldsValue({ numero_orden: sugerido });
      }
      numeroSugeridoRef.current = sugerido;
    } catch {
      // sugerencia no crítica: si falla, el usuario puede escribir el número manualmente
    }
  };

  const openCreateModal = () => {
    setEditingOrden(null);
    form.resetFields();
    form.setFieldsValue({ fecha_pedido: dayjs(), direccion_entrega: DIRECCION_ENTREGA_DEFAULT });
    setSelectedProveedorId(null);
    setProductosProveedor([]);
    setLineItems([]);
    setProductoToAdd(null);
    numeroSugeridoRef.current = '';
    setCreateModalOpen(true);
    fetchNumeroSugerido(null);
  };

  const openEditModal = async (record) => {
    setEditingOrden(record);
    form.resetFields();
    setLineItems([]);
    setProductoToAdd(null);
    setCreateModalOpen(true);
    setEditModalLoading(true);
    try {
      const res = await ordenesCompraService.getById(record.id);
      const orden = res.data.data;

      setSelectedProveedorId(orden.proveedor_id);
      form.setFieldsValue({
        numero_orden: orden.numero_orden,
        codigo: orden.codigo,
        fecha_pedido: orden.fecha_pedido ? dayjs(orden.fecha_pedido) : null,
        fecha_entrega_estimada: orden.fecha_entrega_estimada ? dayjs(orden.fecha_entrega_estimada) : null,
        metodo_envio: orden.metodo_envio,
        direccion_entrega: orden.direccion_entrega || DIRECCION_ENTREGA_DEFAULT,
        notas: orden.notas
      });

      setProductosLoading(true);
      const resProd = await productosService.getAll({ proveedor_id: orden.proveedor_id, activo: 1 });
      setProductosProveedor(resProd.data.data || []);

      setLineItems(
        (orden.detalles || []).map((d) => ({
          producto_id: d.producto_id,
          clave: d.producto?.clave,
          nombre: d.producto?.nombre,
          unidad_medida: d.producto?.unidad_medida,
          cantidad: parseFloat(d.cantidad),
          costo_unitario: parseFloat(d.costo_unitario),
          iva_porcentaje: parseFloat(d.iva_porcentaje)
        }))
      );
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar la orden de compra');
      setCreateModalOpen(false);
    } finally {
      setEditModalLoading(false);
      setProductosLoading(false);
    }
  };

  const handleProveedorChange = async (proveedorId) => {
    setSelectedProveedorId(proveedorId);
    setLineItems([]);
    setProductoToAdd(null);
    if (!editingOrden) fetchNumeroSugerido(proveedorId);
    if (!proveedorId) {
      setProductosProveedor([]);
      return;
    }
    setProductosLoading(true);
    try {
      const res = await productosService.getAll({ proveedor_id: proveedorId, activo: 1 });
      setProductosProveedor(res.data.data || []);
    } catch {
      message.error('Error al cargar productos del proveedor');
    } finally {
      setProductosLoading(false);
    }
  };

  const handleAddLineItem = () => {
    if (!productoToAdd) return;
    if (lineItems.some((li) => li.producto_id === productoToAdd)) {
      message.warning('El producto ya está agregado a la orden');
      return;
    }
    const producto = productosProveedor.find((p) => p.id === productoToAdd);
    if (!producto) return;

    setLineItems([
      ...lineItems,
      {
        producto_id: producto.id,
        clave: producto.clave,
        nombre: producto.nombre,
        unidad_medida: producto.unidad_medida,
        cantidad: 1,
        costo_unitario: parseFloat(producto.precio_compra) || 0,
        iva_porcentaje: 16
      }
    ]);
    setProductoToAdd(null);
  };

  const updateLineItem = (producto_id, field, value) => {
    setLineItems((items) =>
      items.map((li) => (li.producto_id === producto_id ? { ...li, [field]: value } : li))
    );
  };

  const removeLineItem = (producto_id) => {
    setLineItems((items) => items.filter((li) => li.producto_id !== producto_id));
  };

  const calcularLinea = (li) => {
    const subtotal = (parseFloat(li.cantidad) || 0) * (parseFloat(li.costo_unitario) || 0);
    const ivaMonto = subtotal * ((parseFloat(li.iva_porcentaje) || 0) / 100);
    return { subtotal, ivaMonto, total: subtotal + ivaMonto };
  };

  const totalesOrden = lineItems.reduce(
    (acc, li) => {
      const { subtotal, ivaMonto, total } = calcularLinea(li);
      return {
        subtotal: acc.subtotal + subtotal,
        iva: acc.iva + ivaMonto,
        total: acc.total + total
      };
    },
    { subtotal: 0, iva: 0, total: 0 }
  );

  const handleSaveOrden = async () => {
    try {
      const values = await form.validateFields();
      if (lineItems.length === 0) {
        message.warning('Agrega al menos un producto a la orden');
        return;
      }

      const payload = {
        numero_orden: values.numero_orden,
        codigo: values.codigo,
        proveedor_id: selectedProveedorId,
        fecha_pedido: values.fecha_pedido.format('YYYY-MM-DD'),
        fecha_entrega_estimada: values.fecha_entrega_estimada
          ? values.fecha_entrega_estimada.format('YYYY-MM-DD')
          : null,
        metodo_envio: values.metodo_envio,
        direccion_entrega: values.direccion_entrega,
        notas: values.notas,
        detalles: lineItems.map((li) => ({
          producto_id: li.producto_id,
          cantidad: li.cantidad,
          costo_unitario: li.costo_unitario,
          iva_porcentaje: li.iva_porcentaje
        }))
      };

      setSaving(true);
      if (editingOrden) {
        await ordenesCompraService.update(editingOrden.id, payload);
        message.success('Orden de compra actualizada exitosamente');
      } else {
        await ordenesCompraService.create(payload);
        message.success('Orden de compra creada exitosamente');
      }

      setCreateModalOpen(false);
      setEditingOrden(null);
      fetchOrdenes();
    } catch (error) {
      if (error.errorFields) return;
      message.error(
        error.displayMessage || `Error al ${editingOrden ? 'actualizar' : 'crear'} la orden de compra`
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Detalle ─────────────────────────────────────────────────────────────
  const openDetail = async (record) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await ordenesCompraService.getById(record.id);
      setDetailOrden(res.data.data);
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar la orden de compra');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async (id) => {
    const res = await ordenesCompraService.getById(id);
    setDetailOrden(res.data.data);
  };

  const handleAutorizar = async (orden) => {
    setActionLoading(true);
    try {
      await ordenesCompraService.autorizar(orden.id);
      message.success('Orden de compra autorizada');
      await refreshDetail(orden.id);
      fetchOrdenes();
    } catch (error) {
      message.error(error.displayMessage || 'Error al autorizar la orden');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRechazar = async (orden) => {
    setActionLoading(true);
    try {
      await ordenesCompraService.rechazar(orden.id);
      message.success('Orden de compra rechazada');
      await refreshDetail(orden.id);
      fetchOrdenes();
    } catch (error) {
      message.error(error.displayMessage || 'Error al rechazar la orden');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEntregar = async (orden) => {
    setActionLoading(true);
    try {
      const res = await ordenesCompraService.entregar(orden.id);
      message.success(res.data.message || 'Entrega confirmada. Inventario actualizado.');
      await refreshDetail(orden.id);
      fetchOrdenes();
    } catch (error) {
      message.error(error.displayMessage || 'Error al confirmar la entrega');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await ordenesCompraService.delete(record.id);
      message.success(`Orden "${record.numero_orden}" eliminada exitosamente`);
      fetchOrdenes();
    } catch (error) {
      message.error(error.displayMessage || 'Error al eliminar la orden');
    }
  };

  const handlePrint = async (record) => {
    try {
      const res = await ordenesCompraService.getById(record.id);
      setPrintData(res.data.data);
    } catch {
      message.error('Error al preparar la impresión');
    }
  };

  const buildOrdenesSheets = (lista) => {
    const sheetOrdenes = lista.map((o) => ({
      'N° Orden': o.numero_orden,
      'Código': o.codigo || '',
      'Proveedor': o.proveedor?.nombre_comercial || '',
      'Fecha Pedido': o.fecha_pedido ? dayjs(o.fecha_pedido).format('DD/MM/YYYY') : '',
      'Fecha Entrega Estimada': o.fecha_entrega_estimada ? dayjs(o.fecha_entrega_estimada).format('DD/MM/YYYY') : '',
      'Método de Envío': o.metodo_envio || '',
      'Estado': STATUS_CONFIG[o.status]?.label || o.status,
      'Subtotal': parseFloat(o.subtotal) || 0,
      'IVA': parseFloat(o.iva_total) || 0,
      'Total': parseFloat(o.total) || 0,
      'Creada por': o.creador?.nombre || '',
      'Autorizada por': o.autorizador?.nombre || '',
      'Entrega confirmada por': o.entregador?.nombre || ''
    }));

    const sheetDetalle = [];
    lista.forEach((o) => {
      (o.detalles || []).forEach((d) => {
        sheetDetalle.push({
          'N° Orden': o.numero_orden,
          'Proveedor': o.proveedor?.nombre_comercial || '',
          'Clave': d.producto?.clave || '',
          'Producto': d.producto?.nombre || '',
          'Cantidad': parseFloat(d.cantidad) || 0,
          'Unidad': d.producto?.unidad_medida || '',
          'Costo Unitario': parseFloat(d.costo_unitario) || 0,
          'IVA %': parseFloat(d.iva_porcentaje) || 0,
          'Subtotal': parseFloat(d.subtotal) || 0,
          'IVA': parseFloat(d.iva_monto) || 0,
          'Total': parseFloat(d.total) || 0
        });
      });
    });

    return { sheetOrdenes, sheetDetalle };
  };

  const handleExportExcel = () => {
    if (ordenes.length === 0) {
      message.warning('No hay órdenes de compra para exportar');
      return;
    }

    const { sheetOrdenes, sheetDetalle } = buildOrdenesSheets(ordenes);

    const wb = utils.book_new();
    utils.book_append_sheet(wb, utils.json_to_sheet(sheetOrdenes), 'Órdenes de Compra');
    utils.book_append_sheet(wb, utils.json_to_sheet(sheetDetalle), 'Detalle Productos');

    writeFile(wb, `Ordenes_Compra_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
  };

  const handleExportOrdenExcel = async (record) => {
    try {
      const res = await ordenesCompraService.getById(record.id);
      const orden = res.data.data;
      const proveedor = orden.proveedor || {};
      const detalles = orden.detalles || [];
      const fmtFecha = (v) => (v ? dayjs(v).format('DD/MM/YYYY') : '');
      const fmtFechaHora = (v) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '');

      // ── Todo en una sola hoja: cabecera + productos + totales (igual que el impreso) ──
      const filas = [
        ['ORDEN DE COMPRA', orden.numero_orden],
        ['Código', orden.codigo || ''],
        ['Estado', STATUS_CONFIG[orden.status]?.label || orden.status],
        [],
        ['PROVEEDOR'],
        ['Nombre Comercial', proveedor.nombre_comercial || ''],
        ['Código Proveedor', proveedor.codigo || ''],
        ['Contacto', proveedor.contacto_nombre || ''],
        ['Teléfono Contacto', proveedor.contacto_telefono || proveedor.telefono || ''],
        ['Email Contacto', proveedor.contacto_email || proveedor.email || ''],
        ['Dirección', proveedor.direccion || ''],
        [],
        ['DATOS DE LA ORDEN'],
        ['Fecha de Pedido', fmtFecha(orden.fecha_pedido)],
        ['Fecha de Entrega Estimada', fmtFecha(orden.fecha_entrega_estimada)],
        ['Fecha de Autorización', fmtFechaHora(orden.fecha_autorizacion)],
        ['Fecha de Entrega Real', fmtFechaHora(orden.fecha_entrega_real)],
        ['Método de Envío', orden.metodo_envio || ''],
        ['Creada por', orden.creador?.nombre || ''],
        ['Autorizada por', orden.autorizador?.nombre || ''],
        ['Entrega confirmada por', orden.entregador?.nombre || ''],
        ...(orden.status === 'rechazada' ? [['Motivo de Rechazo', orden.motivo_rechazo || '']] : []),
        ['Notas', orden.notas || ''],
        [],
        ['DETALLE DE PRODUCTOS'],
        ['Clave', 'Producto', 'Cantidad', 'Unidad', 'Costo Unitario', 'IVA %', 'Subtotal', 'IVA', 'Total'],
        ...detalles.map((d) => [
          d.producto?.clave || '',
          d.producto?.nombre || '',
          parseFloat(d.cantidad) || 0,
          d.producto?.unidad_medida || '',
          parseFloat(d.costo_unitario) || 0,
          parseFloat(d.iva_porcentaje) || 0,
          parseFloat(d.subtotal) || 0,
          parseFloat(d.iva_monto) || 0,
          parseFloat(d.total) || 0
        ]),
        [],
        ['', '', '', '', '', '', 'Subtotal', '', parseFloat(orden.subtotal) || 0],
        ['', '', '', '', '', '', 'IVA', '', parseFloat(orden.iva_total) || 0],
        ['', '', '', '', '', '', 'Total', '', parseFloat(orden.total) || 0]
      ];

      const ws = utils.aoa_to_sheet(filas);
      ws['!cols'] = [
        { wch: 22 }, { wch: 32 }, { wch: 12 }, { wch: 10 },
        { wch: 15 }, { wch: 10 }, { wch: 13 }, { wch: 13 }, { wch: 13 }
      ];

      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, 'Orden de Compra');

      writeFile(wb, `Orden_Compra_${orden.numero_orden}.xlsx`);
    } catch {
      message.error('Error al generar el Excel de la orden');
    }
  };

  // ── Columnas ────────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'N° Orden',
      dataIndex: 'numero_orden',
      key: 'numero_orden',
      width: 140,
      render: (val) => <Text code strong>{val}</Text>
    },
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 120,
      render: (val) => val ? <Text>{val}</Text> : <Text type="secondary">—</Text>
    },
    {
      title: 'Proveedor',
      key: 'proveedor',
      render: (_, record) => record.proveedor?.nombre_comercial || '—'
    },
    {
      title: 'Fecha Pedido',
      dataIndex: 'fecha_pedido',
      key: 'fecha_pedido',
      width: 120,
      render: (val) => (val ? dayjs(val).format('DD/MM/YYYY') : '—')
    },
    {
      title: 'Entrega Estimada',
      dataIndex: 'fecha_entrega_estimada',
      key: 'fecha_entrega_estimada',
      width: 130,
      render: (val) => (val ? dayjs(val).format('DD/MM/YYYY') : '—')
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      filters: Object.entries(STATUS_CONFIG).map(([value, cfg]) => ({ text: cfg.label, value })),
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <Tag color={STATUS_CONFIG[status]?.color}>{STATUS_CONFIG[status]?.label || status}</Tag>
      )
    },
    {
      title: 'Creada por',
      key: 'creador',
      width: 130,
      render: (_, record) => record.creador?.nombre || '—'
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Ver detalle">
            <Button type="primary" ghost size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)} />
          </Tooltip>
          {canCreate && record.status === 'creada' && (
            <Tooltip title="Editar">
              <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
            </Tooltip>
          )}
          <Tooltip title="Imprimir">
            <Button size="small" icon={<PrinterOutlined />} onClick={() => handlePrint(record)} />
          </Tooltip>
          <Tooltip title="Descargar Excel">
            <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExportOrdenExcel(record)} />
          </Tooltip>
          {canDelete && record.status === 'creada' && (
            <Popconfirm
              title="¿Eliminar orden de compra?"
              description={`La orden "${record.numero_orden}" será eliminada permanentemente.`}
              onConfirm={() => handleDelete(record)}
              okText="Sí, eliminar"
              cancelText="Cancelar"
              icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Eliminar">
                <Button danger size="small" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      {/* Hidden print component */}
      <div style={{ display: 'none' }}>
        <OrdenCompraPrint ref={printRef} orden={printData} />
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Órdenes de Compra</Title>
          <Text type="secondary">Gestión de órdenes de compra a proveedores</Text>
        </div>
      </div>

      {/* Filters & Actions */}
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={10} md={8}>
            <Input
              placeholder="Buscar por N° de orden o proveedor..."
              prefix={<SearchOutlined />}
              allowClear
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (!e.target.value) fetchOrdenes({ search: '' });
              }}
              onPressEnter={(e) => handleSearch(e.target.value)}
              onClear={() => handleSearch('')}
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="Filtrar por estado"
              allowClear
              style={{ width: '100%' }}
              value={statusFilter || undefined}
              onChange={handleStatusFilter}
            >
              {Object.entries(STATUS_CONFIG).map(([value, cfg]) => (
                <Option key={value} value={value}>{cfg.label}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="Filtrar por proveedor"
              allowClear
              showSearch
              style={{ width: '100%' }}
              value={proveedorFilter || undefined}
              onChange={handleProveedorFilter}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {proveedores.map((p) => (
                <Option key={p.id} value={p.id}>{p.nombre_comercial}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={7}>
            <RangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={['Fecha desde', 'Fecha hasta']}
              value={fechaRange}
              onChange={handleFechaRangeFilter}
            />
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => { setSearch(''); setStatusFilter(''); setProveedorFilter(null); setFechaRange(null); fetchOrdenes({ search: '', status: '', proveedor_id: null, fechaRange: null }); }}
            >
              Limpiar
            </Button>
          </Col>
          <Col>
            <Button icon={<FileExcelOutlined />} onClick={handleExportExcel}>
              Descargar Excel
            </Button>
          </Col>
          {canCreate && (
            <Col flex="auto" style={{ textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                Nueva Orden de Compra
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* Table */}
      <Card bordered={false}>
        <Table
          dataSource={ordenes}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 1260 }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '25', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} órdenes`,
            locale: { items_per_page: '/ página' }
          }}
          locale={{ emptyText: 'No se encontraron órdenes de compra' }}
        />
      </Card>

      {/* ── Modal: Nueva / Editar Orden de Compra ────────────────────────── */}
      <Modal
        title={editingOrden ? `Editar Orden de Compra ${editingOrden.numero_orden}` : 'Nueva Orden de Compra'}
        open={createModalOpen}
        onOk={handleSaveOrden}
        onCancel={() => { setCreateModalOpen(false); setEditingOrden(null); }}
        okText={editingOrden ? 'Guardar Cambios' : 'Crear Orden'}
        cancelText="Cancelar"
        confirmLoading={saving}
        width={900}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          style={{ marginTop: 8 }}
          disabled={editModalLoading}
        >
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="numero_orden"
                label="N° de Orden"
                tooltip="Sugerido automáticamente con el consecutivo de la base de datos. Puedes editarlo; si ya existe, se mostrará un error."
                rules={[{ required: true, message: 'El número de orden es requerido' }, { max: 50, message: 'Máximo 50 caracteres' }]}
              >
                <Input placeholder="Ej: 0001-26-PROV" maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="codigo"
                label="Código"
                tooltip="Código o referencia interna de la orden (opcional)"
                rules={[{ max: 50, message: 'Máximo 50 caracteres' }]}
              >
                <Input placeholder="Ej: REQ-2026-045" maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Proveedor" required>
                <Select
                  placeholder="Selecciona un proveedor"
                  showSearch
                  value={selectedProveedorId}
                  onChange={handleProveedorChange}
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {proveedores.map((p) => (
                    <Option key={p.id} value={p.id}>{p.nombre_comercial}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="fecha_pedido"
                label="Fecha de Pedido"
                rules={[{ required: true, message: 'La fecha de pedido es requerida' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="fecha_entrega_estimada" label="Fecha de Entrega Estimada">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="metodo_envio" label="Método de Envío">
                <Select placeholder="Selecciona un método" allowClear>
                  {METODOS_ENVIO.map((m) => (
                    <Option key={m} value={m}>{m}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="notas" label="Notas">
                <TextArea placeholder="Notas adicionales (opcional)" rows={1} maxLength={500} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="direccion_entrega" label="Dirección de Entrega">
                <TextArea rows={2} maxLength={500} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>Productos</Divider>

          <Row gutter={8} align="middle" style={{ marginBottom: 12 }}>
            <Col flex="auto">
              <Select
                placeholder={selectedProveedorId ? 'Selecciona un producto para agregar' : 'Primero selecciona un proveedor'}
                showSearch
                disabled={!selectedProveedorId}
                loading={productosLoading}
                value={productoToAdd}
                onChange={setProductoToAdd}
                style={{ width: '100%' }}
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
                notFoundContent={
                  selectedProveedorId ? 'Este proveedor no tiene productos asociados' : null
                }
              >
                {productosProveedor.map((p) => (
                  <Option key={p.id} value={p.id} disabled={lineItems.some((li) => li.producto_id === p.id)}>
                    {p.clave} — {p.nombre}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Button icon={<PlusOutlined />} onClick={handleAddLineItem} disabled={!productoToAdd}>
                Agregar
              </Button>
            </Col>
          </Row>

          {lineItems.length === 0 ? (
            <Empty description="Sin productos agregados" style={{ margin: '20px 0' }} />
          ) : (
            <Table
              dataSource={lineItems}
              rowKey="producto_id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Producto',
                  key: 'producto',
                  render: (_, li) => (
                    <div>
                      <Text strong style={{ fontSize: 12 }}>{li.clave}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>{li.nombre}</Text>
                    </div>
                  )
                },
                {
                  title: 'Cantidad',
                  key: 'cantidad',
                  width: 110,
                  render: (_, li) => (
                    <InputNumber
                      min={0.01}
                      precision={2}
                      style={{ width: '100%' }}
                      value={li.cantidad}
                      onChange={(v) => updateLineItem(li.producto_id, 'cantidad', v || 0)}
                    />
                  )
                },
                {
                  title: 'Costo Unit.',
                  key: 'costo_unitario',
                  width: 120,
                  render: (_, li) => (
                    <InputNumber
                      min={0}
                      precision={2}
                      prefix="$"
                      style={{ width: '100%' }}
                      value={li.costo_unitario}
                      onChange={(v) => updateLineItem(li.producto_id, 'costo_unitario', v || 0)}
                    />
                  )
                },
                {
                  title: 'IVA %',
                  key: 'iva_porcentaje',
                  width: 90,
                  render: (_, li) => (
                    <InputNumber
                      min={0}
                      max={100}
                      precision={2}
                      style={{ width: '100%' }}
                      value={li.iva_porcentaje}
                      onChange={(v) => updateLineItem(li.producto_id, 'iva_porcentaje', v ?? 0)}
                    />
                  )
                },
                {
                  title: 'Total',
                  key: 'total',
                  width: 110,
                  align: 'right',
                  render: (_, li) => {
                    const { total } = calcularLinea(li);
                    return <Text strong>{formatCurrency(total)}</Text>;
                  }
                },
                {
                  title: '',
                  key: 'acciones',
                  width: 40,
                  render: (_, li) => (
                    <Button
                      danger
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeLineItem(li.producto_id)}
                    />
                  )
                }
              ]}
            />
          )}

          {lineItems.length > 0 && (
            <Row justify="end" style={{ marginTop: 12 }}>
              <Col>
                <Descriptions column={1} size="small" bordered style={{ width: 260 }}>
                  <Descriptions.Item label="Subtotal">{formatCurrency(totalesOrden.subtotal)}</Descriptions.Item>
                  <Descriptions.Item label="IVA">{formatCurrency(totalesOrden.iva)}</Descriptions.Item>
                  <Descriptions.Item label={<Text strong>Total</Text>}>
                    <Text strong>{formatCurrency(totalesOrden.total)}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          )}
        </Form>
      </Modal>

      {/* ── Modal: Detalle de Orden ──────────────────────────────────────── */}
      <Modal
        title={detailOrden ? `Orden de Compra: ${detailOrden.numero_orden}` : 'Orden de Compra'}
        open={detailOpen}
        onCancel={() => { setDetailOpen(false); setDetailOrden(null); }}
        footer={
          <Space>
            <Button onClick={() => { setDetailOpen(false); setDetailOrden(null); }}>Cerrar</Button>
            {detailOrden && canCreate && detailOrden.status === 'creada' && (
              <Button
                icon={<EditOutlined />}
                onClick={() => { setDetailOpen(false); setDetailOrden(null); openEditModal(detailOrden); }}
              >
                Editar
              </Button>
            )}
            {detailOrden && (
              <Button icon={<PrinterOutlined />} onClick={() => handlePrint(detailOrden)}>
                Imprimir
              </Button>
            )}
            {detailOrden && (
              <Button icon={<FileExcelOutlined />} onClick={() => handleExportOrdenExcel(detailOrden)}>
                Descargar Excel
              </Button>
            )}
            {detailOrden && canAutorizar && detailOrden.status === 'creada' && (
              <>
                <Popconfirm
                  title="¿Rechazar orden de compra?"
                  onConfirm={() => handleRechazar(detailOrden)}
                  okText="Sí, rechazar"
                  cancelText="Cancelar"
                  okButtonProps={{ danger: true }}
                >
                  <Button danger icon={<CloseOutlined />} loading={actionLoading}>
                    Rechazar
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title="¿Autorizar orden de compra?"
                  description="Se marcará como autorizada para gestionar el pedido con el proveedor."
                  onConfirm={() => handleAutorizar(detailOrden)}
                  okText="Sí, autorizar"
                  cancelText="Cancelar"
                >
                  <Button type="primary" icon={<CheckOutlined />} loading={actionLoading}>
                    Autorizar
                  </Button>
                </Popconfirm>
              </>
            )}
            {detailOrden && canEntregar && detailOrden.status === 'autorizada' && (
              <Popconfirm
                title="¿Confirmar entrega de la orden?"
                description="Se dará entrada al inventario por los productos de esta orden."
                onConfirm={() => handleEntregar(detailOrden)}
                okText="Sí, confirmar entrega"
                cancelText="Cancelar"
              >
                <Button type="primary" icon={<InboxOutlined />} loading={actionLoading}>
                  Confirmar Entrega
                </Button>
              </Popconfirm>
            )}
          </Space>
        }
        width={800}
        destroyOnClose
      >
        {detailLoading || !detailOrden ? (
          <div style={{ textAlign: 'center', padding: 40 }}>Cargando...</div>
        ) : (
          <div>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Estado" span={2}>
                <Tag color={STATUS_CONFIG[detailOrden.status]?.color}>
                  {STATUS_CONFIG[detailOrden.status]?.label || detailOrden.status}
                </Tag>
                {detailOrden.status === 'rechazada' && detailOrden.motivo_rechazo && (
                  <Text type="secondary"> — {detailOrden.motivo_rechazo}</Text>
                )}
              </Descriptions.Item>
              {detailOrden.codigo && (
                <Descriptions.Item label="Código" span={2}>{detailOrden.codigo}</Descriptions.Item>
              )}
              <Descriptions.Item label="Proveedor">{detailOrden.proveedor?.nombre_comercial}</Descriptions.Item>
              <Descriptions.Item label="Contacto">
                {detailOrden.proveedor?.contacto_nombre || '—'} / {detailOrden.proveedor?.contacto_telefono || detailOrden.proveedor?.telefono || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de Pedido">
                {dayjs(detailOrden.fecha_pedido).format('DD/MM/YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Entrega Estimada">
                {detailOrden.fecha_entrega_estimada ? dayjs(detailOrden.fecha_entrega_estimada).format('DD/MM/YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Método de Envío">{detailOrden.metodo_envio || '—'}</Descriptions.Item>
              {detailOrden.direccion_entrega && (
                <Descriptions.Item label="Dirección de Entrega" span={2}>{detailOrden.direccion_entrega}</Descriptions.Item>
              )}
              <Descriptions.Item label="Creada por">{detailOrden.creador?.nombre || '—'}</Descriptions.Item>
              {detailOrden.autorizador?.nombre && (
                <Descriptions.Item label="Autorizada por">{detailOrden.autorizador.nombre}</Descriptions.Item>
              )}
              {detailOrden.entregador?.nombre && (
                <Descriptions.Item label="Entrega confirmada por">{detailOrden.entregador.nombre}</Descriptions.Item>
              )}
              {detailOrden.notas && (
                <Descriptions.Item label="Notas" span={2}>{detailOrden.notas}</Descriptions.Item>
              )}
            </Descriptions>

            <Divider orientation="left" plain style={{ marginTop: 0 }}>
              <FileTextOutlined /> Productos
            </Divider>
            <Table
              dataSource={detailOrden.detalles || []}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                { title: 'Producto', key: 'nombre', render: (_, d) => d.producto?.nombre || '—' },
                {
                  title: 'Cantidad', key: 'cantidad', width: 110, align: 'right',
                  render: (_, d) => `${parseFloat(d.cantidad).toLocaleString('es-MX')} ${d.producto?.unidad_medida || ''}`
                },
                { title: 'Costo Unit.', key: 'costo', width: 100, align: 'right', render: (_, d) => formatCurrency(d.costo_unitario) },
                { title: 'IVA %', key: 'iva', width: 70, align: 'right', render: (_, d) => `${parseFloat(d.iva_porcentaje)}%` },
                { title: 'Total', key: 'total', width: 110, align: 'right', render: (_, d) => <Text strong>{formatCurrency(d.total)}</Text> }
              ]}
            />

            <Row justify="end" style={{ marginTop: 12 }}>
              <Col>
                <Descriptions column={1} size="small" bordered style={{ width: 260 }}>
                  <Descriptions.Item label="Subtotal">{formatCurrency(detailOrden.subtotal)}</Descriptions.Item>
                  <Descriptions.Item label="IVA">{formatCurrency(detailOrden.iva_total)}</Descriptions.Item>
                  <Descriptions.Item label={<Text strong>Total</Text>}>
                    <Text strong>{formatCurrency(detailOrden.total)}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default OrdenesCompra;
